import { CalendarBlank, Lock } from '@phosphor-icons/react';
import type { WorkbenchCard } from '@mybusinessmate/schema';
import { cx } from '../../lib/cx';
import { TASK_TYPE_META } from '../../lib/task-display';
import {
  cardSubtitle,
  dueText,
  hiddenLine,
  noteLabel,
  unitsText,
  versionPill,
  waitingLine,
} from '../../lib/workbench-display';
import { Icon } from '../Icon';
import { ProgressBar } from '../ui/progress-bar';
import { PriorityPill } from './priority-pill';

/** Figma A05.7 "V": the small outlined version mark ("v2", or "fix" for a proposed fix). */
export function VersionPill({ text }: { text: string }) {
  return (
    <span className="inline-flex h-(--mbm-space-4-5) shrink-0 items-center rounded-(--mbm-radius-xs) border border-border-default px-(--mbm-space-1-5) text-label-small text-text-secondary">
      {text}
    </span>
  );
}

/**
 * One card of the My work board (Figma A05.7 "Card"). The whole card opens the quick peek. A card in "Waiting for QA"
 * or "With client" is not the person's move, so it shows who it is with instead of a date.
 */
export function TaskCard({
  card,
  viewerId,
  selected,
  onOpen,
  now,
}: {
  card: WorkbenchCard;
  viewerId: string | undefined;
  selected: boolean;
  onOpen: () => void;
  now?: Date;
}) {
  const type = TASK_TYPE_META[card.taskType];
  const pill = versionPill(card);
  const label = noteLabel(card);
  const waiting = waitingLine(card, viewerId, now);
  const due = dueText(card.deadline, now);
  const hidden = hiddenLine(card);
  const done = card.progress >= 100;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${card.title}. Open a quick look`}
      aria-pressed={selected}
      className={cx(
        'flex w-full flex-col gap-(--mbm-space-2-5) rounded-(--mbm-component-radius-board-card) bg-surface-card p-(--mbm-space-3-5) text-left',
        'focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)',
        selected && 'outline-2 outline-action-primary',
      )}
    >
      <span className="flex items-center gap-(--mbm-space-1-5)">
        <span className="text-text-secondary">
          <Icon icon={type.icon} size="sm" />
        </span>
        <span className="min-w-0 flex-1 truncate text-body-xsmall text-text-secondary">{type.short}</span>
        <PriorityPill priority={card.priority} />
        {pill ? <VersionPill text={pill} /> : null}
      </span>

      <span className="flex flex-col gap-(--mbm-space-0-5)">
        <span className="text-label-large text-text-primary">{card.title}</span>
        <span className="text-body-xsmall text-text-tertiary">{cardSubtitle(card)}</span>
      </span>

      {label ? (
        <span className="flex flex-col gap-(--mbm-space-0-5) rounded-(--mbm-component-radius-board-note) bg-board-note px-(--mbm-space-2-5) py-(--mbm-space-2)">
          <span className="text-label-small text-tag-amber-text uppercase">{label}</span>
          {card.sentBack?.note ? (
            <span className="line-clamp-3 text-body-xsmall text-board-note-text">{card.sentBack.note}</span>
          ) : null}
        </span>
      ) : null}

      <span className="flex items-center gap-(--mbm-space-2)">
        <ProgressBar value={card.progress} tone={done ? 'success' : 'dark'} label={`${card.title} progress`} />
        <span className="w-(--mbm-space-6) text-right text-label-small text-text-primary">{card.progress}%</span>
      </span>

      <span className="flex items-center gap-(--mbm-space-1-5)">
        <span className={waiting ? 'text-text-tertiary' : due.urgent ? 'text-tag-amber-text' : 'text-text-tertiary'}>
          <Icon icon={CalendarBlank} size="xs" />
        </span>
        <span
          className={cx(
            'min-w-0 flex-1 truncate text-body-xsmall',
            !waiting && due.urgent ? 'text-tag-amber-text' : 'text-text-tertiary',
          )}
        >
          {waiting ?? due.text}
        </span>
        <span className="text-label-small text-text-secondary">{unitsText(card)}</span>
      </span>

      {hidden ? (
        <span className="flex items-center gap-(--mbm-space-1-5) text-text-tertiary">
          <Icon icon={Lock} size="xs" />
          <span className="text-body-xsmall">{hidden}</span>
        </span>
      ) : null}
    </button>
  );
}
